import {
  Button,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus, Pencil, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UnitContextCategory } from '@duke-hq/svc-api-db';

interface Category {
  id?: string;
  categoryName: string;
}

interface UnitContextCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextCategories: Omit<UnitContextCategory, 'torgId'>[];
  refetchCategories: () => void;
}

export function UnitContextCategoryModal({
  isOpen,
  onClose,
  contextCategories,
  refetchCategories,
}: UnitContextCategoryModalProps) {
  const toast = useToast();
  const [newCategory, setNewCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [unitContextCategories, setUnitContextCategories] = useState<Category[]>([]);
  const updateCategoriesMutation = trpcAdmin.unitContextCategories.update.useMutation();
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (contextCategories?.length) {
      setUnitContextCategories(contextCategories);
    }
  }, [contextCategories]);

  const isCategoryDuplicate = (name: string): boolean => {
    const trimmedName = name.trim().toLowerCase();
    return unitContextCategories.some(
      (item) => item.categoryName.trim().toLowerCase() === trimmedName,
    );
  };

  const handleAddCategory = () => {
    const trimmedName = newCategory.trim();
    if (!trimmedName || isCategoryDuplicate(trimmedName)) {
      toast({
        title: 'Category name must be unique',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setUnitContextCategories([...unitContextCategories, { categoryName: trimmedName }]);
    setNewCategory('');
    setIsAddingCategory(false);
  };

  const handleCategoryChange = (id: string | undefined, newValue: string) => {
    setUnitContextCategories((prevCategories) =>
      prevCategories.map((category) =>
        category.id === id ? { ...category, categoryName: newValue } : category,
      ),
    );
  };

  const handleSaveCategory = async () => {
    try {
      const updatedCategories = isAddingCategory
        ? [...unitContextCategories, { categoryName: newCategory }]
        : [...unitContextCategories];

      await updateCategoriesMutation.mutateAsync(updatedCategories);

      toast({
        title: 'Context Categories updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setNewCategory('');
      setIsAddingCategory(false);
      setUnitContextCategories([]);
      onClose();
      await refetchCategories();
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Error updating categories',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    setNewCategory('');
    setIsAddingCategory(false);
    setUnitContextCategories(contextCategories);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Manage Categories</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {unitContextCategories?.map((category, idx) => {
              const trimmedName = category.categoryName.trim().toLowerCase();
              const isDuplicate = unitContextCategories.some(
                (item) =>
                  item.id !== category.id && item.categoryName.trim().toLowerCase() === trimmedName,
              );
              const isInvalid = !trimmedName || isDuplicate;

              return (
                <FormControl key={idx} isInvalid={isInvalid}>
                  <InputGroup>
                    <Input
                      value={category.categoryName}
                      onChange={(e) => handleCategoryChange(category.id, e.target.value)}
                      placeholder={t`Category name`}
                      ref={(el) => {
                        if (category.id) {
                          inputRefs.current[category.id] = el;
                        }
                      }}
                    />
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="Edit category"
                        icon={<Pencil size={iconSize.md} color="gray" />}
                        variant="ghost"
                        size="xs"
                        onClick={() => category.id && inputRefs.current[category.id]?.focus()}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {isInvalid ? (
                    <FormErrorMessage fontSize="sm">
                      {!trimmedName
                        ? t`Category name is required`
                        : t`Category name must be unique`}
                    </FormErrorMessage>
                  ) : null}
                </FormControl>
              );
            })}

            {isAddingCategory ? (
              <FormControl isInvalid={!newCategory.trim() || isCategoryDuplicate(newCategory)}>
                <InputGroup>
                  <Input
                    placeholder={t`Enter category name`}
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      aria-label="Save category"
                      icon={<Check size={iconSize.md} color="gray" />}
                      variant="ghost"
                      size="xs"
                      onClick={handleAddCategory}
                    />
                  </InputRightElement>
                </InputGroup>
                {(!newCategory.trim() || isCategoryDuplicate(newCategory)) && (
                  <FormErrorMessage fontSize="sm">
                    {!newCategory.trim()
                      ? t`Category name is required`
                      : t`Category name must be unique`}
                  </FormErrorMessage>
                )}
              </FormControl>
            ) : (
              <Button
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  setIsAddingCategory(true);
                  setNewCategory('');
                }}
                leftIcon={<Plus size={16} />}
              >
                <Trans>Add New Category</Trans>
              </Button>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} variant="ghost" mr={3}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            colorScheme="brand"
            onClick={() => void handleSaveCategory()}
            isDisabled={isCategoryDuplicate(newCategory) || updateCategoriesMutation.isPending}
            isLoading={updateCategoriesMutation.isPending}
          >
            <Trans>Update</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
